import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { useSearchParams } from 'react-router-dom';
import { addDays, format, parseISO, startOfDay } from 'date-fns';

const SliderDate = ({ pickDate, setPickDate, type }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDate = searchParams.get('date');
  const sliderRef = useRef();
  const [active, setActive] = useState(format(pickDate, 'yyyy-MM-dd'));

  const [initialDate] = useState(() => {
    return urlDate ? parseISO(urlDate) : startOfDay(new Date());
  });

  const dates = Array.from({ length: 31 }, (_, i) => addDays(startOfDay(initialDate), i));

  const getSliderSettings = (type) => {
    const baseSettings = {
      className: 'gap-2',
      dots: false,
      infinite: false,
      speed: 500,
      slidesToScroll: 1,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 10,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 8,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 1,
          },
        },
      ],
    };

    const baseTinySettings = {
      className: 'gap-2',
      dots: false,
      infinite: false,
      speed: 500,
      slidesToScroll: 1,
      slidesToShow: 4,
    };

    switch (type) {
      case 'tiny':
        return baseTinySettings;
      case 'small':
        return {
          ...baseSettings,
          slidesToShow: 8,
          responsive: baseSettings.responsive.map((res) => ({
            ...res,
            settings: {
              ...res.settings,
              slidesToShow: res.breakpoint === 480 ? 4 : 6,
            },
          })),
        };
      default:
        return {
          ...baseSettings,
          slidesToShow: 18,
          responsive: baseSettings.responsive.map((res) => ({
            ...res,
            settings: {
              ...res.settings,
              slidesToShow: res.breakpoint === 480 ? 4 : res.settings.slidesToShow,
            },
          })),
        };
    }
  };

  const settings = {
    ...getSliderSettings(type),
    afterChange: (current) => {
      const newDate = dates[current];
      if (newDate && format(newDate, 'yyyy-MM-dd') !== active) {
        onChoseDate(newDate);
      }
    },
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        const currentIdx = dates.findIndex((d) => format(d, 'yyyy-MM-dd') === active);
        let nextIdx = currentIdx;
        if (e.deltaY > 0 && currentIdx < dates.length - 1) {
          nextIdx = currentIdx + 1;
        } else if (e.deltaY < 0 && currentIdx > 0) {
          nextIdx = currentIdx - 1;
        }
        if (nextIdx !== currentIdx) {
          onChoseDate(dates[nextIdx]);
          if (sliderRef.current) {
            sliderRef.current.slickGoTo(nextIdx);
          }
        }
      }
    };

    const index = dates.findIndex((d) => format(d, 'yyyy-MM-dd') === active);
    if (index > -1 && sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }

    const sliderDiv = document.getElementById('slider-date-scroll');
    if (sliderDiv) {
      sliderDiv.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (sliderDiv) {
        sliderDiv.removeEventListener('wheel', handleWheel);
      }
    };
  }, [active, dates]);

  useEffect(() => {
    if (urlDate) {
      const parsedDate = parseISO(urlDate);
      const isoDate = format(parsedDate, 'yyyy-MM-dd');

      if (format(pickDate, 'yyyy-MM-dd') !== isoDate) {
        setPickDate(parsedDate);
      }
      setActive(isoDate);
    } else {
      setActive(format(pickDate, 'yyyy-MM-dd'));
    }
  }, [urlDate]);

  const onChoseDate = (date) => {
    const isoDate = format(date, 'yyyy-MM-dd');

    if (isoDate === active) return;

    setPickDate(date);
    setActive(isoDate);

    const newParams = new URLSearchParams(searchParams);
    newParams.set('date', isoDate);
    setSearchParams(newParams);
  };

  return (
    <div className="mb-4 px-6" id="slider-date-scroll">
      <Slider ref={sliderRef} {...settings}>
        {dates.map((item, idx) => {
          const isoDate = format(item, 'yyyy-MM-dd');
          const isActive = isoDate === active;

          return (
            <div key={idx} className="w-auto h-auto">
              <div
                onClick={() => onChoseDate(item)}
                className={`${
                  isActive ? 'bg-primary-main text-white' : 'bg-white text-kin-text cursor-pointer'
                } flex flex-col gap-1 justify-center items-center rounded-2xl px-4 py-3 mx-1`}>
                <span className="text-xs">{format(item, 'EEE')}</span>
                <span className="font-semibold text-base">{format(item, 'd')}</span>
                <span className="text-xs">{format(item, 'MMM')}</span>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

export default SliderDate;
